import * as React from "react";

import { cn } from "../utils";

export interface SectionActionsProps {
  /**
   * The SECONDARY controls — view toggles (`<ViewToggle>`), filter buttons, and
   * utility icon buttons. Render each as a ghost icon `<Button size="icon">` (or a
   * `<ViewToggle>`), so the whole cluster shares ONE treatment: no borders, no
   * dividers, the same size and hover. The container only spaces them.
   */
  children?: React.ReactNode;
  /**
   * The single PRIMARY action — the page's "+ New" / create CTA. Rendered last and
   * set apart from the secondary cluster with the emphasized `default` (cobalt)
   * button treatment. At most one; a second primary is the #1 button smell.
   */
  primary?: React.ReactNode;
  className?: string;
}

/**
 * SectionActions — the ONE right-side action bar for a page title (Matt, 2026-07-16).
 * Sits in the `actions` slot of `<SectionTitleBar>`, so a page's controls read the
 * same on every page: SECONDARY controls (view toggle · filter · utility icons) share
 * a single consistent ghost treatment; exactly one PRIMARY action carries the
 * emphasized `default` button. No dividers between controls — the spacing is the
 * grouping. Layout-only: the controls supply their own behavior; this owns the
 * grammar (which button is emphasized, how they sit together), never a re-paint.
 */
export function SectionActions({
  children,
  primary,
  className,
}: SectionActionsProps) {
  return (
    <div
      data-slot="section-actions"
      className={cn("flex flex-wrap items-center gap-1.5", className)}
    >
      {children}
      {primary ?? null}
    </div>
  );
}
